import { Component } from '@angular/core';
import { SearchService } from '../../search/search.service';
import { CoreServiceRegistry } from '../../../core/core-service-registry';
import { SharedServiceRegistry } from '../../shared-service-registry';
import { AlertService } from 'sce-core';
import { SearchParams } from '../../search/models/search-params.model';
import { DataTableConfig } from '../../search/models/data-table-config';
import { DataTableEditorService } from '../../search/results/data-table-editor.service';
import { SearchStatus } from '../../search/models/search-status.model';
import { SearchCriteria, Clause, Condition } from '../../search/models/search-filter-model';
import { ButtonItem } from '../../button/button.model';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';

@Component({
  selector: 'lfwms-populate-from-load-or-orders-popup',
  templateUrl: './populate-from-load-or-orders-popup.component.html',
  styleUrls: ['./populate-from-load-or-orders-popup.component.css'],
})
export class PopulateFromLoadOrOrdersPopupComponent {

  public searchService: SearchService;
  public searchParams: SearchParams;
  public searchStatus: SearchStatus;
  private http$ = this.coreServiceRegistry.http$;
  public resourceService = this.coreServiceRegistry.resourceService;
  public showLookup: boolean = false;
  public type: string;
  public eventSubject = new Subject<any>();
  public setCount: number = 0;
  public qtyCase: number = 0;
  public grossWeight: number = 0; // gross weight = sum of weight of selected rows
  public cube: number = 0; // cube = sum of cube of selected rows
  public buttonArray: any = [
     new ButtonItem(
      'Populate',
      this.resourceService.get('lbl_populate'),
      'shipref_populate',
      'icon_populate.svg',
      []
    ),
    new ButtonItem(
      "Populate All - Filtered",
      this.resourceService.get("btn_populate_all_filtered"),
      "shipref_populate_all_filtered",
      "icon_populate.svg",
      []
    ),
  ];

  constructor(
    private coreServiceRegistry: CoreServiceRegistry,
    private sharedServiceRegistry: SharedServiceRegistry,
    private alertService: AlertService) {
    this.eventSubject = new Subject<any>();
  }

  public initializeComponents(type: string, facility: string) {
    this.type = type;
    const tabledId = type === 'load' ? 'mbolpplldplntd' : 'mbolppltype2td';
    this.searchParams = this.createLookupParamsForColumn(tabledId);
    this.searchParams.alertService = this.alertService;
    const searchStatus = new SearchStatus();
    this.searchService = this.sharedServiceRegistry.createSearchService(this.searchParams, searchStatus);
    this.searchService.setSearchCriteria(this.createSearchCriteria(facility));
    this.searchStatus = this.searchService.searchStatus;
  }

  protected createLookupParamsForColumn(tableId: string) {
    const searchParams = new SearchParams();
    searchParams.tableId = tableId;
    searchParams.isFullFetch = true;
    searchParams.columnCount = 10;
    const tableConfig: DataTableConfig = new DataTableConfig();
    tableConfig.supportFiltering = true;
    searchParams.dataTableConfig = tableConfig;
    searchParams.actionButtons = this.sharedServiceRegistry.buttonFactoryService.loadButtonsByPrivilege(this.buttonArray);
    const tableEditor: DataTableEditorService = new DataTableEditorService(this.coreServiceRegistry,
      this.sharedServiceRegistry);
    searchParams.tableEditor = tableEditor;
    const searchStatus = new SearchStatus();
    return searchParams;
  }

  public createSearchCriteria(facility: string) {
    const defaultSearchCriteria = new SearchCriteria();
    defaultSearchCriteria.conditions = [];
    defaultSearchCriteria.loadDefaultCriteria = false;

    const defaultCondition = this.createSearchCondition(1);
    defaultCondition.isReadOnly = false;
    defaultCondition.isMandatory = true;
    defaultCondition.operator = 'AND'; // [LFWM-2892][To make the logical operation 'AND' ]
    const defaultClause = new Clause();
    defaultClause.column = 'facility';
    defaultClause.value = facility;
    defaultClause.operation = '=';
    defaultClause.logicalOperation = '';
    defaultCondition.clauses = [defaultClause];
    defaultSearchCriteria.conditions.push(defaultCondition);
    return defaultSearchCriteria;
  }

  /**
   * create an instance of condition
   * @param id
   */
  protected createSearchCondition(id: any) {
    const defaultCondition = new Condition();
    defaultCondition.id = id;
    defaultCondition.displayMode = 'VIEW';
    return defaultCondition;
  }

  public handleButtonAction($event) {
    // Currently only populate button
    switch ($event) {
      case "Populate": {
        this.handlePopulateAction();
        break;
      }
      case "Populate All - Filtered": {
        this.handlePopulateAllFilteredAction();
        break;
      }
    }
  }

  public handlePopulateAllFilteredAction() {
    const selectedRows = this.searchService.getAllRows();

    if (selectedRows.length === 0) {
      this.alertService.error(this.resourceService.get("msg_sel_detail"));
      return;
    }

    const searchCriteria = this.searchService.formatSearchCriteria(
      this.searchService.getSearchCriteria(),
      null,
      this.searchService.getFilterCondition()
    );

    const populateEvent = {
      type: "populateAllFiltered",
      searchCriteria: searchCriteria,
    };
    this.notifyEvent(populateEvent);
  }	  

  public handlePopulateAction() {
    const selectedRows = this.searchService.getSelectedRows();

    if (selectedRows.length === 0) {
      this.alertService.error(this.resourceService.get('msg_sel_detail'));
      return;
    }

    const requiredDetailsToPopulateLineItems = [];
    selectedRows.forEach((row: any) => {
      requiredDetailsToPopulateLineItems.push(
        row.cells['orderKey'].cVal);
    });

    const populateEvent = { 'type': 'populate', 'selectedItems': requiredDetailsToPopulateLineItems };
    this.notifyEvent(populateEvent);
  }

  public notifyEvent(value: any) {
    this.eventSubject.next(value);
  }

  public receiveEvent(): Observable<any> {
    return this.eventSubject.asObservable();
  }

  /* event is captured from result data table component on select of checkbox,
  if the row level checkbox is enabled or disabled then the rowIndex also passed */
  public onSelectCheckbox(rowIndex: any) {
    const selectedRows: any[] = this.searchService.getSelectedRows();
    this.setCount = 0;
    this.qtyCase = 0;
    this.grossWeight = 0;
    this.cube = 0;
    if (this.type === 'load' && selectedRows.length > 0) {
      selectedRows.forEach(row => {
        this.setCount += parseInt(row.cells['gohSet'].cVal, 10);
        this.qtyCase += parseInt(row.cells['qtyCase'].cVal, 10);
        this.grossWeight += parseInt(row.cells['weight'].cVal, 10);
        this.cube += parseInt(row.cells['cube'].cVal, 10);
      });
      this.qtyCase = this.qtyCase - this.setCount;
    }
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/1_SCE_CI_CD_SIT/src/app/shared/lookup/populate-from-load-or-orders-popup/populate-from-load-or-orders-popup.component.ts